import { Gender, GenderSchema, GenderType, ProfileDetail } from "@garther/interfaces";
import { Avatar, Box, Button, Spinner } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { PenLine, Save, X } from "lucide-react";
import { useRef, useState } from "react";

import { useAuthStore } from "@/features/auth/auth.store";
import { useProfileByUserQuery, useUploadAvatar } from "@/features/profile/hooks/use-profile-query";

interface BasicInfoProps {
  userId: string;
  isOwner: boolean;
}

interface LocalUserProfile {
  name: string;
  gender: GenderType;
  dateOfBirth: string;
  phone: string;
}

// Age validation utility
const validateAge = (dateString: string): string | undefined => {
  if (!dateString) return;

  const birthDate = new Date(dateString);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  const actualAge =
    monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

  if (actualAge > 100) return "You must be 100 years old or younger";
  if (actualAge < 0) return "Date of birth cannot be in the future";

  return;
};

// Form field configuration types
type FormFieldConfig = {
  name: keyof LocalUserProfile;
  label: string;
  type: "text" | "date" | "select";
  editable: boolean;
  placeholder?: string;
  options?: Array<{ value: GenderType; label: string }>;
  validator?: (value: string) => string | undefined;
};

const FORM_FIELDS: FormFieldConfig[] = [
  {
    name: "name",
    label: "profile.basicInfo.username",
    type: "text",
    editable: true,
    placeholder: "profile.basicInfo.enterYourName"
  },
  {
    name: "gender",
    label: "profile.basicInfo.gender",
    type: "select",
    editable: true,
    options: [
      { value: Gender.MALE, label: "profile.creation.genderOptions.male" },
      { value: Gender.FEMALE, label: "profile.creation.genderOptions.female" },
      { value: Gender.OTHER, label: "profile.creation.genderOptions.other" },
      {
        value: Gender.PREFER_NOT_TO_SAY,
        label: "profile.creation.genderOptions.preferNotToSay"
      }
    ]
  },
  {
    name: "dateOfBirth",
    label: "profile.basicInfo.dateOfBirth",
    type: "date",
    editable: true,
    validator: validateAge
  },
  {
    name: "phone",
    label: "profile.basicInfo.phone",
    type: "text",
    editable: true,
    placeholder: "profile.basicInfo.enterYourPhone"
  }
];

const defaultValues: LocalUserProfile = {
  name: "",
  gender: Gender.PREFER_NOT_TO_SAY,
  dateOfBirth: "",
  phone: ""
};

export default function BasicInfo({ userId, isOwner }: BasicInfoProps) {
  const { t, formatDate } = useI18n();

  const { curUser, updateProfile, setProfile } = useAuthStore();

  const { data: otherUserProfile, isLoading: isLoadingOtherProfile } = useProfileByUserQuery(userId, {
    enabled: !isOwner
  });

  const uploadAvatarMutation = useUploadAvatar({
    onSuccess: (updatedProfile) => {
      setProfile(updatedProfile);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploadingAvatar = uploadAvatarMutation.isPending;

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (!userId || !isOwner) return;

      setSubmitError(null);

      try {
        await updateProfile({
          ...value,
          gender: GenderSchema.parse(value.gender)
        });
        setIsEditing(false);
      } catch (err) {
        console.error("Error saving profile:", err);
        setSubmitError(t("profile.basicInfo.failedToSaveProfile"));
      }
    }
  });

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!curUser.profile) return;
    form.setFieldValue("name", curUser.profile.name);
    form.setFieldValue("gender", curUser.profile.gender ?? Gender.PREFER_NOT_TO_SAY);
    form.setFieldValue("dateOfBirth", curUser.profile.dateOfBirth ?? "");
    form.setFieldValue("phone", curUser.profile.phone ?? "");
    setIsEditing(true);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    form.reset();
    setIsEditing(false);
    setSubmitError(null);
  };

  const handleAvatarClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isOwner && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("profile.basicInfo.invalidFileType"));
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error(t("profile.basicInfo.fileTooLarge"));
      return;
    }

    uploadAvatarMutation.mutate(file);
  };

  const renderViewValue = (fieldConfig: FormFieldConfig, viewProfile: ProfileDetail) => {
    let displayValue: string;

    if (fieldConfig.name === "dateOfBirth") {
      displayValue = viewProfile.dateOfBirth
        ? formatDate(new Date(viewProfile.dateOfBirth), {
            year: "numeric",
            month: "short",
            day: "numeric"
          })
        : t("profile.basicInfo.notProvided");
    } else if (fieldConfig.name === "gender") {
      displayValue = t(
        `profile.creation.genderOptions.${(viewProfile.gender ?? "prefer_not_to_say").toLowerCase()}`
      );
    } else {
      displayValue =
        (viewProfile[fieldConfig.name as keyof typeof viewProfile] as string) ||
        t("profile.basicInfo.notProvided");
    }

    return <span className="text-textcolor-primary text-responsive-base">{displayValue}</span>;
  };

  const renderEditField = (fieldConfig: FormFieldConfig) => {
    const fieldName = fieldConfig.name;

    return (
      <form.Field
        name={fieldName}
        validators={
          fieldConfig.validator
            ? {
                onChange: ({ value }) => fieldConfig.validator!(value)
              }
            : undefined
        }
      >
        {(field) => (
          <div className="flex flex-col w-[40%] min-w-[200px]">
            {fieldConfig.type === "select" ? (
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(GenderSchema.parse(e.target.value))}
                className="text-responsive-base text-textcolor-primary bg-transparent border-b border-border-light focus:outline-none focus:border-border-dark focus:bg-background-highlight hover:cursor-pointer hover:bg-background-highlight"
                disabled={form.state.isSubmitting}
              >
                {fieldConfig.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={fieldConfig.type}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-responsive-base text-textcolor-primary bg-transparent border-b border-border-light focus:outline-none focus:border-border-dark focus:bg-background-highlight hover:cursor-pointer hover:bg-background-highlight"
                placeholder={fieldConfig.placeholder ? t(fieldConfig.placeholder) : undefined}
                disabled={form.state.isSubmitting}
              />
            )}
            {field.state.meta.errors && (
              <span className="text-red-500 text-xs mt-1">{field.state.meta.errors.join(", ")}</span>
            )}
          </div>
        )}
      </form.Field>
    );
  };

  // For non-owner viewing: show loading / unavailable states
  if (!isOwner) {
    if (isLoadingOtherProfile) {
      return (
        <div className="space-y-4">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-textcolor-secondary mt-2">{t("profile.basicInfo.loadingProfile")}</p>
          </div>
        </div>
      );
    }

    if (!otherUserProfile) {
      return (
        <div className="space-y-4">
          <div className="text-center py-8">
            <p className="text-textcolor-secondary mt-2">{t("profile.basicInfo.cannotLoadProfile")}</p>
          </div>
        </div>
      );
    }
  }

  // For owner: gate on Zustand profile being loaded
  if (isOwner && !curUser.profile) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-textcolor-secondary mt-2">{t("profile.basicInfo.cannotLoadProfile")}</p>
        </div>
      </div>
    );
  }

  const viewProfile = isOwner ? curUser.profile! : otherUserProfile!;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Box
        className="flex flex-col justify-stretch items-start gap-4"
        label={t("profile.basicInfo.aboutYou")}
      >
        <div className="flex justify-start items-center gap-6 w-full">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="relative">
            <Avatar name={viewProfile.name ?? undefined} src={viewProfile.avatar ?? undefined} size="2xl" />
            <Button
              variant="ghost"
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="text-text-secondary !p-1.5 absolute bottom-0 right-0 bg-background-secondary border border-border rounded-full"
            >
              {isUploadingAvatar ? <Spinner className="w-3 h-3" /> : <PenLine className="w-3 h-3" />}
            </Button>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start flex-1 gap-2">
            <div className="flex flex-col justify-center items-start">
              <p className="text-text-primary text-responsive-md font-normal">{viewProfile.email}</p>
              <p className="text-text-secondary text-responsive-base font-normal">
                {t("profile.basicInfo.joinedOn") +
                  " " +
                  formatDate(viewProfile.createdAt, {
                    year: "numeric",
                    month: "short",
                    day: "numeric"
                  })}
              </p>
            </div>
            {isOwner && (
              <div className="flex flex-col justify-center items-end">
                <div className="flex justify-center items-center space-x-2 h-full">
                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        type="submit"
                        isDisabled={form.state.isSubmitting}
                        className="text-text-primary !px-2 !py-1"
                      >
                        {form.state.isSubmitting ? (
                          <Spinner className="w-4 h-4" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span className="text-responsive-base ml-2">{t("profile.basicInfo.save")}</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        isDisabled={form.state.isSubmitting}
                        className="text-text-secondary !px-2 !py-1 border-border"
                      >
                        <X className="w-4 h-4" />
                        <span className="text-responsive-base ml-2">{t("profile.basicInfo.cancel")}</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleEdit}
                      className="text-text-secondary border-border !px-2 !py-1"
                    >
                      <PenLine className="w-4 h-4" />
                      <span className="text-responsive-base ml-2">{t("profile.basicInfo.editProfile")}</span>
                    </Button>
                  )}
                </div>
                {submitError && (
                  <div className="mt-2 text-red-600 w-full text-responsive-base text-end">{submitError}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 w-full">
          <div className="space-y-2">
            {FORM_FIELDS.map((fieldConfig) => (
              <div
                key={fieldConfig.name}
                className="flex justify-between items-center py-1 border-b border-border-light"
              >
                <span className="text-text-secondary text-responsive-base">{t(fieldConfig.label)}</span>
                {isEditing && isOwner
                  ? renderEditField(fieldConfig)
                  : renderViewValue(fieldConfig, viewProfile)}
              </div>
            ))}
          </div>
        </div>
      </Box>
    </form>
  );
}
